/**
 * Mobile Camera Optimization Utilities
 * Provides enhanced camera handling for mobile devices
 */

import { cameraLogger } from './logger';
import { getCameraErrorName } from './cameraErrorHandler';

/**
 * A media request may continue to another profile only when the browser says
 * constraint negotiation failed. Permission, security, availability, state,
 * abort, timeout, and unknown failures cannot be repaired by changing the
 * requested resolution and must retain their original identity.
 */
function isConstraintNegotiationFailure(error: unknown): boolean {
  return getCameraErrorName(error) === 'OverconstrainedError';
}

export interface MobileCameraConfig {
  facingMode: 'user' | 'environment';
  optimizeForDocument?: boolean;
  enableAutoFocus?: boolean;
  enableExposureControl?: boolean;
  quality?: 'low' | 'medium' | 'high';
}

export interface CameraCapabilities {
  hasFlash: boolean;
  hasAutoFocus: boolean;
  hasExposureControl: boolean;
  supportedResolutions: { width: number; height: number }[];
  maxZoom: number;
  supportedFormats: string[];
}

export interface CameraNegotiationDiagnostics {
  mode: 'selfie' | 'document';
  requestedProfileName: string;
  fallbackLevel: number;
  totalProfiles: number;
  settingsWidth: number | null;
  settingsHeight: number | null;
  videoWidth: number | null;
  videoHeight: number | null;
  isBelowThreshold: boolean;
  accepted: boolean;
  acceptanceReason:
    | 'meets_threshold'
    | 'meets_threshold_via_video_probe'
    | 'no_track_dimensions_available'
    | 'final_fallback'
    | 'document_capture'
    | 'desktop_capture'
    | 'unknown';
}

type ResolutionProfile = {
  min?: number;
  ideal: number;
  max?: number;
};

type ConstraintProfile = {
  width: ResolutionProfile;
  height: ResolutionProfile;
};

type ConstraintRequestProfile = ConstraintProfile & {
  profileName: string;
};

const SELFIE_CONSTRAINT_PROFILES = {
  mobile: {
    low: {
      width: { ideal: 640 },
      height: { ideal: 480 },
    },
    medium: {
      width: { ideal: 960 },
      height: { ideal: 540 },
    },
    high: {
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
  },
  desktop: {
    low: {
      width: { ideal: 960 },
      height: { ideal: 540 },
    },
    medium: {
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
    high: {
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
  },
} as const satisfies Record<
  'mobile' | 'desktop',
  Record<'low' | 'medium' | 'high', ConstraintProfile>
>;

const DOCUMENT_CONSTRAINT_PROFILES = {
  mobile: {
    low: {
      width: { min: 640, ideal: 960, max: 1280 },
      height: { min: 480, ideal: 720, max: 960 },
    },
    medium: {
      width: { min: 960, ideal: 1280, max: 1920 },
      height: { min: 720, ideal: 960, max: 1440 },
    },
    high: {
      width: { min: 1280, ideal: 1600, max: 2048 },
      height: { min: 960, ideal: 1200, max: 1536 },
    },
  },
  desktop: {
    low: {
      width: { min: 1280, ideal: 1600, max: 1920 },
      height: { min: 720, ideal: 900, max: 1080 },
    },
    medium: {
      width: { min: 1440, ideal: 1920, max: 2560 },
      height: { min: 900, ideal: 1080, max: 1440 },
    },
    high: {
      width: { min: 1920, ideal: 2560, max: 3840 },
      height: { min: 1080, ideal: 1440, max: 2160 },
    },
  },
} as const satisfies Record<
  'mobile' | 'desktop',
  Record<'low' | 'medium' | 'high', ConstraintProfile>
>;

const IOS_SELFIE_REQUEST_LADDERS = {
  low: [
    {
      profileName: 'ios_selfie_low_preferred',
      width: { ideal: 1280 },
      height: { ideal: 960 },
    },
    {
      profileName: 'ios_selfie_low_fallback',
      width: { ideal: 960 },
      height: { ideal: 720 },
    },
  ],
  medium: [
    {
      profileName: 'ios_selfie_medium_preferred',
      width: { ideal: 1600 },
      height: { ideal: 1200 },
    },
    {
      profileName: 'ios_selfie_medium_desktop_like',
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
    {
      profileName: 'ios_selfie_medium_fallback',
      width: { ideal: 1280 },
      height: { ideal: 960 },
    },
  ],
  high: [
    {
      profileName: 'ios_selfie_preferred_4_3_capped',
      width: { ideal: 1280 },
      height: { ideal: 960 },
    },
    {
      profileName: 'ios_selfie_preferred_hd_capped',
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
    {
      profileName: 'ios_selfie_fallback_4_3',
      width: { ideal: 960 },
      height: { ideal: 720 },
    },
  ],
} as const satisfies Record<
  'low' | 'medium' | 'high',
  ConstraintRequestProfile[]
>;

const MOBILE_SELFIE_REQUEST_LADDERS = {
  low: [
    {
      profileName: 'mobile_selfie_low_desktop_like',
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
    {
      profileName: 'mobile_selfie_low_fallback',
      width: { ideal: 960 },
      height: { ideal: 540 },
    },
  ],
  medium: [
    {
      profileName: 'mobile_selfie_medium_desktop_like',
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
    {
      profileName: 'mobile_selfie_medium_4_3',
      width: { ideal: 1600 },
      height: { ideal: 1200 },
    },
    {
      profileName: 'mobile_selfie_medium_fallback',
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
  ],
  high: [
    {
      profileName: 'mobile_selfie_preferred_4_3_capped',
      width: { ideal: 1280 },
      height: { ideal: 960 },
    },
    {
      profileName: 'mobile_selfie_preferred_hd_capped',
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
    {
      profileName: 'mobile_selfie_fallback_4_3',
      width: { ideal: 960 },
      height: { ideal: 720 },
    },
  ],
} as const satisfies Record<
  'low' | 'medium' | 'high',
  ConstraintRequestProfile[]
>;

const SELFIE_EMERGENCY_FALLBACK_PROFILE: ConstraintRequestProfile = {
  profileName: 'selfie_emergency_fallback',
  width: { ideal: 640 },
  height: { ideal: 480 },
};

const MOBILE_SELFIE_MIN_ACCEPTABLE_SHORT_EDGE = 720;
const MOBILE_SELFIE_MIN_ACCEPTABLE_PIXEL_COUNT = 1280 * 720;
const SELFIE_DIMENSION_PROBE_TIMEOUT_MS = 750;

export class MobileCameraOptimizer {
  private stream: MediaStream | null = null;
  private videoTrack: MediaStreamTrack | null = null;
  private capabilities: CameraCapabilities | null = null;
  private negotiationDiagnostics: CameraNegotiationDiagnostics | null = null;
  private isIOS: boolean;
  private isAndroid: boolean;
  private isMobile: boolean;

  constructor() {
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    this.isAndroid = /Android/.test(navigator.userAgent);
    this.isMobile = this.isIOS || this.isAndroid || window.innerWidth < 600;
  }

  /**
   * Get optimized camera constraints for mobile devices
   */
  getOptimizedConstraints(config: MobileCameraConfig): MediaStreamConstraints {
    const profile = this.getConstraintProfile(config);
    return this.buildConstraints(config, profile);
  }

  /**
   * Build a getUserMedia constraint object from a specific profile.
   */
  private buildConstraints(
    config: MobileCameraConfig,
    profile: ConstraintProfile
  ): MediaStreamConstraints {
    const baseConstraints: MediaStreamConstraints = {
      video: {
        facingMode: config.facingMode,
        width: profile.width,
        height: profile.height,
        frameRate: this.getOptimalFrameRate(),
        ...this.getMobileSpecificConstraints(config),
      },
    };

    return baseConstraints;
  }

  /**
   * Initialize camera with mobile optimizations
   */
  async initializeCamera(config: MobileCameraConfig): Promise<MediaStream> {
    try {
      this.cleanup();

      const stream = this.shouldUseSelfieFallbackLadder(config)
        ? await this.initializeSelfieCameraWithFallbacks(config)
        : await this.initializeStandardCamera(config);

      this.stream = stream;
      this.videoTrack = this.stream.getVideoTracks()[0];

      // Validate that we have an active video track
      if (!this.videoTrack || this.videoTrack.readyState !== 'live') {
        throw new Error('Video track is not active');
      }

      await this.detectCapabilities();
      await this.applyOptimalSettings(config);

      return this.stream;
    } catch (error) {
      cameraLogger.error('Camera initialization failed:', { error: error instanceof Error ? error : new Error(`${error}`) });
      // Rethrow the original value untouched. Callers classify on
      // DOMException.name (NotAllowedError, NotFoundError, ...), and they read
      // it by duck typing rather than `instanceof Error` precisely so a
      // cross-realm DOMException still classifies. Normalizing to a new Error
      // here would rename it to 'Error' and undo that.
      throw error;
    }
  }

  private async initializeStandardCamera(
    config: MobileCameraConfig
  ): Promise<MediaStream> {
    const constraints = this.getOptimizedConstraints(config);
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.setNegotiationDiagnostics({
        mode: config.optimizeForDocument ? 'document' : 'selfie',
        requestedProfileName: config.optimizeForDocument
          ? 'document_default'
          : 'desktop_selfie_default',
        fallbackLevel: 1,
        totalProfiles: 1,
        settingsWidth: null,
        settingsHeight: null,
        videoWidth: null,
        videoHeight: null,
        isBelowThreshold: false,
        accepted: true,
        acceptanceReason: config.optimizeForDocument
          ? 'document_capture'
          : 'desktop_capture',
      });
      return stream;
    } catch (error) {
      if (!isConstraintNegotiationFailure(error)) {
        throw error;
      }
      cameraLogger.warn(
        'Optimal constraints failed, trying fallback:',
        { error: error instanceof Error ? error : new Error(`${error}`) }
      );
      return this.getFallbackStream(config.facingMode);
    }
  }

  private async initializeSelfieCameraWithFallbacks(
    config: MobileCameraConfig
  ): Promise<MediaStream> {
    const ladder = this.getSelfieConstraintRequests(config);
    let lastError: unknown = null;

    for (const [index, profile] of ladder.entries()) {
      const fallbackLevel = index + 1;
      const constraints = this.buildConstraints(config, profile);

      cameraLogger.info('Requesting selfie stream profile', {
        labels: {
          profileName: profile.profileName,
          fallbackLevel: `${fallbackLevel}`,
          totalProfiles: `${ladder.length}`,
          constraints: JSON.stringify(constraints),
        },
      });

      try {
        const attemptStream = await navigator.mediaDevices.getUserMedia(
          constraints
        );
        const attemptTrack = attemptStream.getVideoTracks()[0];

        if (!attemptTrack || attemptTrack.readyState !== 'live') {
          this.stopAttemptStream(attemptStream);
          throw new Error('Video track is not active');
        }

        const diagnostics = await this.inspectNegotiatedSelfieTrack(
          attemptStream,
          attemptTrack,
          profile.profileName,
          fallbackLevel,
          ladder.length
        );

        cameraLogger.info('Negotiated selfie stream settings', {
          labels: {
            profileName: diagnostics.requestedProfileName,
            fallbackLevel: `${diagnostics.fallbackLevel}`,
            totalProfiles: `${diagnostics.totalProfiles}`,
            settingsWidth:
              diagnostics.settingsWidth === null
                ? 'unknown'
                : `${diagnostics.settingsWidth}`,
            settingsHeight:
              diagnostics.settingsHeight === null
                ? 'unknown'
                : `${diagnostics.settingsHeight}`,
            isBelowThreshold: `${diagnostics.isBelowThreshold}`,
            accepted: `${diagnostics.accepted}`,
            acceptanceReason: diagnostics.acceptanceReason,
          },
        });

        if (!diagnostics.accepted && fallbackLevel < ladder.length) {
          cameraLogger.warn(
            'Negotiated selfie stream below threshold, escalating to next fallback profile',
            {
              labels: {
                profileName: diagnostics.requestedProfileName,
                fallbackLevel: `${diagnostics.fallbackLevel}`,
                settingsWidth:
                  diagnostics.settingsWidth === null
                    ? 'unknown'
                    : `${diagnostics.settingsWidth}`,
                settingsHeight:
                  diagnostics.settingsHeight === null
                    ? 'unknown'
                    : `${diagnostics.settingsHeight}`,
              },
            }
          );
          this.stopAttemptStream(attemptStream);
          continue;
        }

        this.setNegotiationDiagnostics(diagnostics);
        return attemptStream;
      } catch (error) {
        lastError = error;
        if (!isConstraintNegotiationFailure(error)) {
          throw error;
        }
        cameraLogger.warn('Selfie stream profile failed, trying next fallback', {
          error: error instanceof Error ? error : new Error(`${error}`),
          labels: {
            profileName: profile.profileName,
            fallbackLevel: `${fallbackLevel}`,
            totalProfiles: `${ladder.length}`,
          },
        });
      }
    }

    if (!isConstraintNegotiationFailure(lastError)) {
      throw lastError;
    }

    cameraLogger.warn(
      'All configured selfie profiles failed, using emergency fallback stream',
      {
        error: lastError instanceof Error ? lastError : new Error(`${lastError}`),
        labels: {
          profileName: SELFIE_EMERGENCY_FALLBACK_PROFILE.profileName,
        },
      }
    );

    const emergencyStream = await this.getFallbackStream(config.facingMode);
    this.setNegotiationDiagnostics({
      mode: 'selfie',
      requestedProfileName: SELFIE_EMERGENCY_FALLBACK_PROFILE.profileName,
      fallbackLevel: ladder.length + 1,
      totalProfiles: ladder.length + 1,
      settingsWidth: null,
      settingsHeight: null,
      videoWidth: null,
      videoHeight: null,
      isBelowThreshold: true,
      accepted: true,
      acceptanceReason: 'final_fallback',
    });
    return emergencyStream;
  }

  /**
   * Apply mobile-specific video element optimizations
   */
  optimizeVideoElement(video: HTMLVideoElement): void {
    // Mobile-specific attributes
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.muted = true;
    video.autoplay = true;

    // Prevent zoom and enhance touch
    video.style.touchAction = 'none';
    video.style.userSelect = 'none';
    video.style.webkitUserSelect = 'none';

    // iOS specific optimizations
    if (this.isIOS) {
      video.style.webkitTransform = 'translateZ(0)';
      video.style.transform = 'translateZ(0)';
    }

    // Android specific optimizations
    if (this.isAndroid) {
      video.style.objectFit = 'cover';
    }

    // Add load event handler for mobile
    video.addEventListener('loadedmetadata', () => {
      // Small delay for mobile orientation changes
      setTimeout(() => {
        if (video.paused) {
          video
            .play()
            .catch((err) => cameraLogger.warn('Video play failed:', err));
        }
      }, 100);
    });

    // Handle orientation changes
    this.handleOrientationChanges(video);
  }

  /**
   * Enable advanced camera controls if available
   */
  async enableAdvancedControls(video: HTMLVideoElement): Promise<void> {
    if (!this.videoTrack || !this.capabilities) return;

    try {
      // Settings and constraints available if needed
      // const settings = this.videoTrack.getSettings();
      // const constraints = this.videoTrack.getConstraints();

      // Enable auto-focus if available
      if (this.capabilities.hasAutoFocus) {
        await this.videoTrack.applyConstraints({
          // @ts-expect-error - focusMode is not in TypeScript types yet
          advanced: [{ focusMode: 'continuous' }],
        });
      }

      // Enable exposure control if available
      if (this.capabilities.hasExposureControl) {
        await this.videoTrack.applyConstraints({
          // @ts-expect-error - exposureMode is not in TypeScript types yet
          advanced: [{ exposureMode: 'continuous' }],
        });
      }

      // Add tap-to-focus for mobile
      if (this.isMobile) {
        this.addTapToFocus(video);
      }
    } catch (error) {
      cameraLogger.warn('Advanced controls not available:', { error: error instanceof Error ? error : new Error(`${error}`) });
    }
  }

  /**
   * Handle device orientation changes
   */
  private handleOrientationChanges(video: HTMLVideoElement): void {
    const handleOrientationChange = () => {
      setTimeout(() => {
        // Refresh video dimensions
        if (video.srcObject) {
          const stream = video.srcObject as MediaStream;
          const track = stream.getVideoTracks()[0];
          if (track) {
            const settings = track.getSettings();
            cameraLogger.debug(
              'Orientation changed, video settings:',
              { labels: { settings: JSON.stringify(settings) }}
            );
          }
        }
      }, 200);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);

    // Cleanup
    const cleanup = () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };

    // Store cleanup function using a WeakMap instead of direct property assignment
    interface VideoWithCleanup extends HTMLVideoElement {
      _orientationCleanup?: () => void;
    }
    (video as VideoWithCleanup)._orientationCleanup = cleanup;
  }

  /**
   * Add tap-to-focus functionality
   */
  private addTapToFocus(video: HTMLVideoElement): void {
    video.addEventListener('click', async (event) => {
      if (!this.videoTrack) return;

      try {
        const rect = video.getBoundingClientRect();
        // Focus coordinates calculated but not used in simplified implementation
        // const x = (event.clientX - rect.left) / rect.width;
        // const y = (event.clientY - rect.top) / rect.height;

        // Apply focus point if supported
        if ('focusDistance' in this.videoTrack.getConstraints()) {
          await this.videoTrack.applyConstraints({
            advanced: [
              {
                // Using focusMode property which is not in standard types yet
                focusMode: 'single-shot',
              } as MediaTrackConstraints,
            ],
          });

          // Visual feedback
          this.showFocusIndicator(
            video,
            event.clientX - rect.left,
            event.clientY - rect.top
          );
        }
      } catch (error) {
        cameraLogger.warn('Tap-to-focus not supported:', { error: error instanceof Error ? error : new Error(`${error}`) });
      }
    });
  }

  /**
   * Show focus indicator at tap location
   */
  private showFocusIndicator(
    video: HTMLVideoElement,
    x: number,
    y: number
  ): void {
    const indicator = document.createElement('div');
    indicator.style.cssText = `
      position: absolute;
      top: ${y}px;
      left: ${x}px;
      width: 60px;
      height: 60px;
      border: 2px solid var(--brand-primary);
      border-radius: 50%;
      transform: translate(-50%, -50%);
      pointer-events: none;
      z-index: 1000;
      animation: focusPulse 1s ease-out;
    `;

    const container = video.parentElement;
    if (container) {
      container.style.position = 'relative';
      container.appendChild(indicator);

      // Add CSS animation if not present
      if (!document.querySelector('#focus-animation')) {
        const style = document.createElement('style');
        style.id = 'focus-animation';
        style.textContent = `
          @keyframes focusPulse {
            0% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
            25% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            75% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0; }
          }
        `;
        document.head.appendChild(style);
      }

      setTimeout(() => indicator.remove(), 1000);
    }
  }

  private getConstraintProfile(config: MobileCameraConfig): ConstraintProfile {
    const quality = config.quality || 'medium';
    const deviceClass = this.isMobile ? 'mobile' : 'desktop';
    const profiles = config.optimizeForDocument
      ? DOCUMENT_CONSTRAINT_PROFILES
      : SELFIE_CONSTRAINT_PROFILES;

    return profiles[deviceClass][quality];
  }

  /**
   * Get optimal frame rate
   */
  private getOptimalFrameRate(): ConstrainDouble {
    if (this.isMobile) {
      return { ideal: 30, max: 30 };
    }
    return { ideal: 30, max: 60 };
  }

  /**
   * Get mobile-specific constraints
   */
  private getMobileSpecificConstraints(
    config: MobileCameraConfig
  ): MediaTrackConstraints {
    const constraints: MediaTrackConstraints = {};

    if (this.isIOS) {
      constraints.frameRate = { ideal: 30, max: 30 };
      if (config.enableAutoFocus) {
        // focusMode is not in standard MediaTrackConstraints
        (
          constraints as MediaStreamConstraints & { focusMode?: string }
        ).focusMode = 'continuous';
      }
    }

    if (this.isAndroid && config.optimizeForDocument) {
      constraints.aspectRatio = {
        ideal: 4 / 3,
      };
      if (config.enableExposureControl) {
        // exposureMode is not in standard MediaTrackConstraints
        (
          constraints as MediaStreamConstraints & { exposureMode?: string }
        ).exposureMode = 'continuous';
      }
    }

    return constraints;
  }

  /**
   * Get fallback stream with basic constraints
   */
  private async getFallbackStream(
    facingMode: 'user' | 'environment'
  ): Promise<MediaStream> {
    const basicConstraints: MediaStreamConstraints = {
      video: {
        facingMode,
        width: { ideal: 640 },
        height: { ideal: 480 },
      },
    };

    return navigator.mediaDevices.getUserMedia(basicConstraints);
  }

  private shouldUseSelfieFallbackLadder(config: MobileCameraConfig): boolean {
    return this.isMobile && !config.optimizeForDocument && config.facingMode === 'user';
  }

  private getSelfieConstraintRequests(
    config: MobileCameraConfig
  ): ConstraintRequestProfile[] {
    const quality = config.quality || 'medium';
    return this.isIOS
      ? [...IOS_SELFIE_REQUEST_LADDERS[quality]]
      : [...MOBILE_SELFIE_REQUEST_LADDERS[quality]];
  }

  private async inspectNegotiatedSelfieTrack(
    stream: MediaStream,
    track: MediaStreamTrack,
    profileName: string,
    fallbackLevel: number,
    totalProfiles: number
  ): Promise<CameraNegotiationDiagnostics> {
    let settingsWidth: number | null = null;
    let settingsHeight: number | null = null;

    try {
      const settings = track.getSettings();
      settingsWidth = typeof settings.width === 'number' ? settings.width : null;
      settingsHeight =
        typeof settings.height === 'number' ? settings.height : null;
    } catch (error) {
      cameraLogger.warn('Could not inspect negotiated selfie track settings', {
        error: error instanceof Error ? error : new Error(`${error}`),
        labels: {
          profileName,
          fallbackLevel: `${fallbackLevel}`,
        },
      });
    }

    let measuredVideoWidth: number | null = null;
    let measuredVideoHeight: number | null = null;

    if (settingsWidth === null || settingsHeight === null) {
      const measuredDimensions = await this.measureStreamDimensions(stream);
      measuredVideoWidth = measuredDimensions.width;
      measuredVideoHeight = measuredDimensions.height;
    }

    const effectiveWidth = settingsWidth ?? measuredVideoWidth;
    const effectiveHeight = settingsHeight ?? measuredVideoHeight;

    if (effectiveWidth === null || effectiveHeight === null) {
      return {
        mode: 'selfie',
        requestedProfileName: profileName,
        fallbackLevel,
        totalProfiles,
        settingsWidth,
        settingsHeight,
        videoWidth: measuredVideoWidth,
        videoHeight: measuredVideoHeight,
        isBelowThreshold: false,
        accepted: fallbackLevel === totalProfiles,
        acceptanceReason:
          fallbackLevel === totalProfiles
            ? 'final_fallback'
            : 'no_track_dimensions_available',
      };
    }

    const isBelowThreshold = this.isSelfieResolutionBelowThreshold(
      effectiveWidth,
      effectiveHeight
    );

    return {
      mode: 'selfie',
      requestedProfileName: profileName,
      fallbackLevel,
      totalProfiles,
      settingsWidth,
      settingsHeight,
      videoWidth: measuredVideoWidth,
      videoHeight: measuredVideoHeight,
      isBelowThreshold,
      accepted: !isBelowThreshold || fallbackLevel === totalProfiles,
      acceptanceReason:
        !isBelowThreshold
          ? measuredVideoWidth !== null && measuredVideoHeight !== null
            ? 'meets_threshold_via_video_probe'
            : 'meets_threshold'
          : fallbackLevel === totalProfiles
            ? 'final_fallback'
            : 'unknown',
    };
  }

  private async measureStreamDimensions(
    stream: MediaStream
  ): Promise<{ width: number | null; height: number | null }> {
    return new Promise((resolve) => {
      const probeVideo = document.createElement('video');
      let settled = false;

      const settle = (width: number | null, height: number | null) => {
        if (settled) {
          return;
        }
        settled = true;
        probeVideo.pause();
        probeVideo.srcObject = null;
        probeVideo.onloadedmetadata = null;
        clearTimeout(timeoutId);
        resolve({ width, height });
      };

      const timeoutId = window.setTimeout(() => {
        settle(null, null);
      }, SELFIE_DIMENSION_PROBE_TIMEOUT_MS);

      probeVideo.muted = true;
      probeVideo.playsInline = true;
      probeVideo.autoplay = false;
      probeVideo.srcObject = stream;
      probeVideo.onloadedmetadata = () => {
        settle(
          probeVideo.videoWidth || null,
          probeVideo.videoHeight || null
        );
      };

      void probeVideo.play().catch(() => {
        // Metadata may still load without playback; keep waiting for timeout/onloadedmetadata.
      });
    });
  }

  private isSelfieResolutionBelowThreshold(
    width: number,
    height: number
  ): boolean {
    const shortEdge = Math.min(width, height);
    const pixelCount = width * height;
    return (
      shortEdge < MOBILE_SELFIE_MIN_ACCEPTABLE_SHORT_EDGE ||
      pixelCount < MOBILE_SELFIE_MIN_ACCEPTABLE_PIXEL_COUNT
    );
  }

  private stopAttemptStream(stream: MediaStream): void {
    stream.getTracks().forEach((track) => track.stop());
  }

  private setNegotiationDiagnostics(
    diagnostics: CameraNegotiationDiagnostics
  ): void {
    this.negotiationDiagnostics = diagnostics;
  }

  /**
   * Detect camera capabilities
   */
  private async detectCapabilities(): Promise<void> {
    if (!this.videoTrack) return;

    try {
      const capabilities = this.videoTrack.getCapabilities();

      this.capabilities = {
        hasFlash: 'torch' in capabilities,
        hasAutoFocus: 'focusMode' in capabilities,
        hasExposureControl: 'exposureMode' in capabilities,
        supportedResolutions: this.extractResolutions(capabilities),
        maxZoom:
          (capabilities as MediaTrackCapabilities & { zoom?: { max?: number } })
            .zoom?.max || 1,
        supportedFormats: ['webm', 'mp4'], // Simplified
      };
    } catch (error) {
      cameraLogger.warn('Could not detect camera capabilities:', { error: error instanceof Error ? error : new Error(`${error}`) });
      this.capabilities = {
        hasFlash: false,
        hasAutoFocus: false,
        hasExposureControl: false,
        supportedResolutions: [],
        maxZoom: 1,
        supportedFormats: [],
      };
    }
  }

  /**
   * Extract supported resolutions from capabilities
   */
  private extractResolutions(
    capabilities: MediaTrackCapabilities
  ): { width: number; height: number }[] {
    const resolutions = [];

    if (capabilities.width && capabilities.height) {
      const widthRange = capabilities.width;
      const heightRange = capabilities.height;

      // Common mobile resolutions
      const commonResolutions = [
        { width: 640, height: 480 },
        { width: 1280, height: 720 },
        { width: 1920, height: 1080 },
      ];

      for (const res of commonResolutions) {
        if (
          res.width >= (widthRange.min || 0) &&
          res.width <= (widthRange.max || Infinity) &&
          res.height >= (heightRange.min || 0) &&
          res.height <= (heightRange.max || Infinity)
        ) {
          resolutions.push(res);
        }
      }
    }

    return resolutions;
  }

  /**
   * Apply optimal settings based on config
   */
  private async applyOptimalSettings(
    config: MobileCameraConfig
  ): Promise<void> {
    if (!this.videoTrack || !this.capabilities) return;

    try {
      const settings: Record<string, unknown> = {};

      // Apply auto-focus if available and enabled
      if (config.enableAutoFocus && this.capabilities.hasAutoFocus) {
        settings.focusMode = 'continuous';
      }

      // Apply exposure control if available and enabled
      if (
        config.enableExposureControl &&
        this.capabilities.hasExposureControl
      ) {
        settings.exposureMode = 'continuous';
      }

      if (Object.keys(settings).length > 0) {
        await this.videoTrack.applyConstraints({ advanced: [settings] });
      }
    } catch (error) {
      cameraLogger.warn('Could not apply optimal settings:', { error: error instanceof Error ? error : new Error(`${error}`) });
    }
  }

  /**
   * Get current camera capabilities
   */
  getCapabilities(): CameraCapabilities | null {
    return this.capabilities;
  }

  /**
   * Get the most recent negotiation diagnostics for the active stream.
   */
  getNegotiationDiagnostics(): CameraNegotiationDiagnostics | null {
    return this.negotiationDiagnostics;
  }

  /**
   * Record the rendered video element dimensions for diagnostics after metadata loads.
   */
  recordVideoElementDimensions(
    width: number,
    height: number
  ): CameraNegotiationDiagnostics | null {
    if (!this.negotiationDiagnostics) {
      return null;
    }

    const updatedDiagnostics: CameraNegotiationDiagnostics = {
      ...this.negotiationDiagnostics,
      videoWidth: width,
      videoHeight: height,
      isBelowThreshold:
        this.negotiationDiagnostics.mode === 'selfie'
          ? this.isSelfieResolutionBelowThreshold(width, height)
          : this.negotiationDiagnostics.isBelowThreshold,
    };

    this.negotiationDiagnostics = updatedDiagnostics;
    return updatedDiagnostics;
  }

  /**
   * Get current video track
   */
  getVideoTrack(): MediaStreamTrack | null {
    return this.videoTrack;
  }

  /**
   * Switch camera if multiple cameras available
   */
  async switchCamera(): Promise<MediaStream | null> {
    if (!this.stream) return null;

    const currentFacingMode = this.videoTrack?.getSettings().facingMode;
    const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';

    try {
      // Stop current stream
      this.cleanup();

      // Start new stream with switched camera
      const config: MobileCameraConfig = {
        facingMode: newFacingMode,
        optimizeForDocument: newFacingMode === 'environment',
      };

      return await this.initializeCamera(config);
    } catch (error) {
      cameraLogger.error('Camera switch failed:', { error: error instanceof Error ? error : new Error(`${error}`) });
      throw new Error('Failed to switch camera');
    }
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.videoTrack = null;
    this.capabilities = null;
    this.negotiationDiagnostics = null;
  }

  /**
   * Check if device is mobile
   */
  static isMobile(): boolean {
    return (
      /iPad|iPhone|iPod|Android/.test(navigator.userAgent) ||
      window.innerWidth < 600
    );
  }

  /**
   * Check if device is iOS
   */
  static isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  /**
   * Check if device is Android
   */
  static isAndroid(): boolean {
    return /Android/.test(navigator.userAgent);
  }
}
